import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, BadgeCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-rose-500 text-sm";

export function ListingActions({ entityType, entityId, claimStatus = "unclaimed", verified = false, variant = "default" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [following, setFollowing] = useState(false);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [claim, setClaim] = useState({ name: user?.name || "", email: user?.email || "", phone: "", relationship: "", proof_link: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entityId) return;
    api.get(`/follow/status?entity_type=${entityType}&entity_id=${entityId}`)
      .then(({ data }) => { setFollowing(data.following); setCount(data.count); }).catch(() => {});
  }, [entityType, entityId, user]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Log in to follow listings"); nav("/login"); return; }
    try {
      const { data } = await api.post("/follow", { entity_type: entityType, entity_id: entityId });
      setFollowing(data.following); setCount(data.count);
      toast.success(data.following ? "Following — you'll get updates" : "Unfollowed");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitClaim = async () => {
    if (!user) { toast.error("Log in to claim this listing"); nav("/login"); return; }
    setBusy(true);
    try {
      await api.post("/claims", { entity_type: entityType, entity_id: entityId, ...claim });
      toast.success("Claim submitted — our team will verify and grant you access.");
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const compact = variant === "compact";

  return (
    <div className={`flex items-center gap-3 ${compact ? "" : "flex-wrap"}`}>
      <button onClick={toggleFollow} data-testid="follow-btn"
        className={`flex items-center gap-2 font-bold rounded-full transition-all ${compact ? "px-4 py-2 text-sm" : "px-5 py-2.5"} ${following ? "bg-rose-500 text-white" : "glass hover:border-white/30"}`}>
        <Heart size={16} fill={following ? "currentColor" : "none"} />
        {following ? "Following" : "Follow"}
        {count > 0 && <span className="text-xs opacity-80">· {count}</span>}
      </button>

      {verified ? (
        <span className="flex items-center gap-1.5 text-sm text-emerald-300/90" data-testid="verified-badge">
          <BadgeCheck size={16} /> Verified listing
        </span>
      ) : claimStatus === "claimed" ? (
        <span className="flex items-center gap-1.5 text-sm text-zinc-300/90" data-testid="claimed-badge">
          <ShieldCheck size={16} /> Claimed
        </span>
      ) : claimStatus === "pending" ? (
        <span className="flex items-center gap-1.5 text-sm text-amber-300/80" data-testid="claim-pending-badge">
          <ShieldCheck size={16} /> Claim pending review
        </span>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="claim-btn" className={`flex items-center gap-2 font-medium rounded-full glass hover:border-white/30 transition-all ${compact ? "px-4 py-2 text-sm" : "px-5 py-2.5"}`}>
              <ShieldCheck size={16} /> Claim this {entityType === "artist" ? "artist profile" : entityType === "venue" ? "venue profile" : "listing"}
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#111] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2"><ShieldCheck className="text-rose-400" size={22} /> Claim this {entityType}</DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-sm text-zinc-400 -mt-1">Are you the owner, manager or authorized representative? Submit a request and our team will verify you and hand over the keys to manage this page.</DialogDescription>
            <div className="space-y-3 mt-2">
              <input className={inp} placeholder="Your full name" value={claim.name} onChange={(e) => setClaim({ ...claim, name: e.target.value })} data-testid="claim-name" />
              <input className={inp} placeholder="Email" value={claim.email} onChange={(e) => setClaim({ ...claim, email: e.target.value })} data-testid="claim-email" />
              <input className={inp} placeholder="Phone (optional)" value={claim.phone} onChange={(e) => setClaim({ ...claim, phone: e.target.value })} data-testid="claim-phone" />
              <select className={inp} value={claim.relationship} onChange={(e) => setClaim({ ...claim, relationship: e.target.value })} data-testid="claim-relationship">
                <option value="">Relationship to this listing…</option>
                {["Owner", "Manager", "Band member", "Promoter", "Authorized representative", "Other"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <input className={inp} placeholder="Website or social link that proves your connection (optional)" value={claim.proof_link} onChange={(e) => setClaim({ ...claim, proof_link: e.target.value })} data-testid="claim-proof" />
              <textarea rows={3} className={inp} placeholder="Anything else we should know?" value={claim.message} onChange={(e) => setClaim({ ...claim, message: e.target.value })} data-testid="claim-message" />
              <button onClick={submitClaim} disabled={busy || !claim.name || !claim.email} className="w-full bg-rose-500 hover:bg-rose-600 font-bold py-3 rounded-lg disabled:opacity-40" data-testid="claim-submit">{busy ? "Submitting..." : "Submit claim request"}</button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
